"use client"

import { useEffect, useState } from "react"
import { usePathname } from "next/navigation"

/**
 * Unread count for the Glym chat icon badge (/api/glym/notifications).
 *
 * Polls slowly on purpose. The things this counts — an admin flipping an
 * announcement campaign live, Glym saying something unprompted — happen on a
 * scale of hours, not seconds, so a tight poll would buy nothing and cost a
 * request per open tab. 2 minutes keeps a fresh partnership announcement
 * visible well within a browsing session.
 *
 * Returns 0 while the user is on /glym: they're looking at the chat, so a
 * badge on the button that brought them there is meaningless. Refetches on
 * leaving, which is also when the server has just stamped last_read_at.
 */
const POLL_MS = 2 * 60 * 1000

export function useGlymUnread(): number {
  const pathname = usePathname()
  const [count, setCount] = useState(0)
  const onGlymPage = pathname?.startsWith("/glym") ?? false

  useEffect(() => {
    if (onGlymPage) {
      setCount(0)
      return
    }

    let cancelled = false
    const load = async () => {
      try {
        const res = await fetch("/api/glym/notifications")
        if (!res.ok) return
        const data = await res.json()
        if (!cancelled) setCount(Number(data?.count) || 0)
      } catch {
        // Badge only — a failed poll leaves the last known count alone.
      }
    }

    load()
    const interval = setInterval(load, POLL_MS)
    return () => {
      cancelled = true
      clearInterval(interval)
    }
  }, [onGlymPage, pathname])

  return onGlymPage ? 0 : count
}
